/**
 * sms-dialog: the ONE shared layout of a member's SMS conversation, COPIED from Frontend G's
 * `v1\src\lib\sms-dialog.ts` (branch s1-h-g, commit 6f8704d; the written layout is Portal Frontend
 * `v1\_docs\26.09.24 01.14.30 PM, rev1, SMS Dialog Shared Layout ...md`). His word, 24 Sep 2026 1:07 PM:
 * every member's detail shows the dialog the way ClariConnect's Live Activity Feed "By member" view does;
 * the office's 1:40 PM brief: Care's member view reuses it. The functions below the line "Care's adapter"
 * are Care's own: they turn the console's SMS transcript (cdx-sms `/sms/thread`, SmsMessageRecord) and the
 * member's Care entries into the shared DialogText shape. Where Frontend G's copy and this one differ,
 * theirs wins and this is corrected. NO em dashes. ASCII hyphens only.
 */
import type { SmsMessageRecord, SmsReplyClass } from './care-model';

/** One text, as the Texts panel's read serves it on either side (sms_comms today, portal.member_comms on the master). */
export interface DialogText {
  message_id: string;
  campaign: string | null;
  phone: string | null;
  direction: 'out' | 'in' | string | null;
  text: string | null;
  template_version: string | null;
  template_language: string | null;
  template_key: string | null;
  attempt: number | null;
  kind: string | null;
  status: string | null;
  classification: string | null;
  intent: string | null;
  at: string | null;
}

export type ChipTone = 'kit' | 'decline' | 'stop' | 'help' | 'other';
export interface ReplyChip { label: string; tone: ChipTone }

/**
 * The chip beside a reply, named as the feed names it: OPTIN_1 -> kit_request, DECLINE_2 -> decline, STOP -> stop,
 * HELP -> help; an OTHER reply shows the intent the platform read (or "other"); no chip when nothing was read.
 */
export function replyChip(t: Pick<DialogText, 'classification' | 'intent'>): ReplyChip | null {
  const intent = (t.intent ?? '').trim();
  switch ((t.classification ?? '').trim().toUpperCase()) {
    case 'OPTIN_1': return { label: 'kit_request', tone: 'kit' };
    case 'DECLINE_2': return { label: 'decline', tone: 'decline' };
    case 'STOP': return { label: 'stop', tone: 'stop' };
    case 'HELP': return { label: 'help', tone: 'help' };
    case 'OTHER': return { label: intent || 'other', tone: 'other' };
    default: return intent ? { label: intent, tone: 'other' } : null;
  }
}

/** The status word on an outbound line: FAILED and REJECTED set apart (the feed's purple), the rest plain. */
export function statusTone(status: string | null | undefined): 'plain' | 'fail' {
  const s = (status ?? '').trim().toUpperCase();
  return s === 'FAILED' || s === 'REJECTED' ? 'fail' : 'plain';
}

/** The line's arrow: the feed's right arrow for a text out, left arrow for a reply in. */
export function directionArrow(direction: string | null | undefined): string {
  return direction === 'in' ? '⬅' : '➡';
}

/**
 * The feed's time, "MM-DD HH:MM:SS" in the viewer's local time (the mock prints "09-21 12:54:46"); the stored text
 * when it does not parse, empty when there is none.
 */
export function fmtDialogTime(at: string | null | undefined): string {
  const v = (at ?? '').trim();
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** How many lines went out and came in. */
export function dialogCounts(lines: ReadonlyArray<Pick<DialogText, 'direction'>>): { out: number; in: number } {
  let out = 0;
  let inn = 0;
  for (const l of lines) { if (l.direction === 'in') inn++; else out++; }
  return { out, in: inn };
}

/** The feed keeps this many newest lines open; older ones fold behind "N earlier lines". */
export const DIALOG_FOLD = 6;

/** The fold: everything when opened or short enough, else the newest `keep` lines and the count of the folded ones. */
export function foldDialog<T>(lines: readonly T[], open: boolean, keep: number = DIALOG_FOLD): { shown: T[]; hidden: number } {
  if (open || lines.length <= keep) return { shown: [...lines], hidden: 0 };
  return { shown: lines.slice(lines.length - keep), hidden: lines.length - keep };
}

/** The line above an outbound text: "template_key -> STATUS", the attempt after the key when it is a retry. */
export function outboundLead(t: Pick<DialogText, 'template_key' | 'kind' | 'attempt' | 'status'>): { what: string; status: string } {
  const key = (t.template_key ?? '').trim() || (t.kind ?? '').trim() || 'text';
  const attempt = t.attempt !== null && t.attempt !== undefined && t.attempt > 1 ? ` #${t.attempt}` : '';
  return { what: `${key}${attempt}`, status: (t.status ?? '').trim() || '-' };
}

/* ------------------------------------------ Care's adapter ------------------------------------------ */

/**
 * Care's reply classes in the feed's words, so `replyChip` names a Care reply exactly as the feed does:
 * YES_KIT is the feed's OPTIN_1 and DECLINE_CAMPAIGN its DECLINE_2; STOP, HELP and OTHER are the same word.
 */
export const CARE_CLASS_TO_FEED: Readonly<Record<SmsReplyClass, string>> = {
  YES_KIT: 'OPTIN_1',
  DECLINE_CAMPAIGN: 'DECLINE_2',
  STOP: 'STOP',
  HELP: 'HELP',
  OTHER: 'OTHER',
};

/** What Care knows of a reply from the member's entries: the platform's class and intent, by the platform's message id. */
export interface ReplyReadingById { classification: SmsReplyClass; intent: string | null; campaign: string | null }

/**
 * One transcript message as a shared DialogText, oldest-first ordering left to the caller. A reply's chip
 * comes from the member's Care entry with the SAME platform message id (the feed's own reading); failing
 * that, a STOP or HELP keyword the transcript carries; otherwise no chip (nothing was read). Never guessed
 * from the words.
 */
export function dialogFromCare(m: SmsMessageRecord, readings: ReadonlyMap<string, ReplyReadingById>): DialogText {
  const inbound = m.direction === 'in';
  const read = inbound ? readings.get(m.messageId) : undefined;
  const kw = (m.keyword ?? '').trim().toUpperCase();
  const classification = read ? CARE_CLASS_TO_FEED[read.classification] : inbound && (kw === 'STOP' || kw === 'HELP') ? kw : null;
  return {
    message_id: m.messageId,
    campaign: read?.campaign ?? null,
    phone: m.fromPhone ?? null,
    direction: inbound ? 'in' : 'out',
    text: m.body ?? '',
    template_version: null,
    template_language: m.detectedLang ?? null,
    template_key: inbound ? null : m.templateKey ?? null,
    attempt: null,
    kind: inbound ? null : m.automated ? 'campaign' : m.operator ? 'console' : null,
    status: inbound ? null : m.deliveryStatus ?? null,
    classification,
    intent: read?.intent ?? null,
    at: m.ts,
  };
}

/** The transcript as dialog lines, oldest first, ties broken by the message id (the shared order: at, then id). */
export function dialogLines(messages: readonly SmsMessageRecord[], readings: ReadonlyMap<string, ReplyReadingById>): DialogText[] {
  return messages
    .map((m) => dialogFromCare(m, readings))
    .sort((a, b) => (a.at ?? '').localeCompare(b.at ?? '') || a.message_id.localeCompare(b.message_id));
}
